import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type UpdateUserPayload, type UpdateUserResponse } from './types';
import {
  zodUpdateUserEndpointSchema,
  zodUpdateUserSchema,
} from './validations';

/**
 * Updates a user with a given userId as ADMIN.
 *
 * @param {Object} userData - The user data for updating a user.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Admin/operation/update_user_admin_users__id__put
 */
export const updateUserAdmin = async (
  userId: UUID,
  userData: UpdateUserPayload,
) => {
  try {
    const response = await vestigasApi
      .put(ENDPOINT.USER.UPDATE_ADMIN(userId), {
        json: userData,
        validate: {
          body: zodUpdateUserSchema,
          pathSegments: zodUpdateUserEndpointSchema,
        },
      })
      .json<UpdateUserResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error updating user',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.user.updateUserAdmin.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.user.updateUserAdmin.error.generic, {
            ns: 'data',
          });
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
